/**
 * The dayjs adapter — APX-560 (PR-1). The ONE module allowed to import dayjs;
 * every date-touching helper migrates onto these exports (PR-2..6).
 *
 * Behavior is pinned to moment parity by the PR-0 spike
 * (tests/unit/helper/dayjs-parity-spike.test.ts) and the verdict ledger
 * (docs/dates/dayjs-parity-ledger.md):
 * - S-05: strict-ISO parse recipe (raw dayjs parse rolls over out-of-range parts).
 * - S-06: Intl timezone guard with an explicit offset-string rejection.
 * - S-04: tzdb `zz` abbreviations via the frozen lookup + in-repo plugin below.
 * - S-07: DST wall-clock rebuild (`wallClockAtHour`).
 * - S-08: moment's month-bubbling duration decomposition (`durationParts`).
 * - S-01: hand-rolled calendar bucketing vs the tz-adjusted reference day.
 */
import dayjs, { type Dayjs, type PluginFunc } from 'dayjs';
import en from 'dayjs/locale/en';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { ZONE_ABBREVIATIONS } from './date-zone-abbreviations';

/** A date value produced by this adapter. Alias so consumers never name dayjs. */
export type AppDate = Dayjs;

export const isAppDate = (value: unknown): value is AppDate => dayjs.isDayjs(value);

/**
 * Request the strict-ISO parse recipe: callers pass this symbol to `utcToZoned` in the
 * `givenFormat` slot (the standalone replacement for the old `moment.ISO_8601`).
 */
export const ISO_8601: unique symbol = Symbol('AppDate.ISO_8601');

export type DateParseFormat = typeof ISO_8601 | string;

// ---------------------------------------------------------------------------------
// S-06 — timezone validity guard (Intl try/catch + bare-offset rejection).
// Modern Intl ACCEPTS bare offsets like '+05:00'; moment.tz.zone() does not.
// ---------------------------------------------------------------------------------
export const isValidTimezone = (timezone?: string | null): boolean => {
  if (!timezone || /^[+-]\d{2}:?\d{2}$/.test(timezone)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------------
// S-05 — strict-ISO parse recipe. dayjs's fast-path parser ROLLS OVER out-of-range
// parts ('2026-13-45' becomes a valid 2027 date), so the recipe range-checks the
// calendar parts itself. Numbered capture groups (1..6 = year..second) — the repo
// targets ES2017, which predates named groups.
// ---------------------------------------------------------------------------------
const ISO_EXTENDED =
  /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:[T ](\d{2})(?::(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?)?(?:Z|[+-]\d{2}:?\d{2})?)?)?)?$/;
const ISO_BASIC = /^(\d{4})(\d{2})(\d{2})$/;

const matchIsoShape = (input: string): RegExpExecArray | null =>
  ISO_EXTENDED.exec(input) ?? ISO_BASIC.exec(input);

const hasValidIsoParts = (match: RegExpExecArray): boolean => {
  const [, year, month, day, hour, minute, second] = match;
  if (month) {
    const monthNum = Number(month);
    if (monthNum < 1 || monthNum > 12) return false;
    if (day) {
      const dayNum = Number(day);
      const daysInMonth = dayjs.utc(`${year}-${month}-01`).daysInMonth();
      if (dayNum < 1 || dayNum > daysInMonth) return false;
    }
  }
  if (hour && Number(hour) > 23) return false;
  if (minute && Number(minute) > 59) return false;
  if (second && Number(second) > 59) return false;
  return true;
};

/** Strict-ISO parse in UTC mode — replaces `moment.utc(x, moment.ISO_8601)`. */
export const parseStrictIso = (input: string): AppDate | null => {
  const match = matchIsoShape(input);
  if (!match || !hasValidIsoParts(match)) return null;
  const parsed = dayjs.utc(input);
  return parsed.isValid() ? parsed : null;
};

/** Strict-ISO parse in local mode — replaces `moment(x, moment.ISO_8601, true)`. */
export const parseStrictIsoLocal = (input: string): AppDate | null => {
  const match = matchIsoShape(input);
  if (!match || !hasValidIsoParts(match)) return null;
  const parsed = dayjs(input);
  return parsed.isValid() ? parsed : null;
};

// ---------------------------------------------------------------------------------
// GAP-1 — lenient parse dispatch. ISO-shaped strings take the STRICT result (null on
// range violations, killing dayjs's silent rollover); everything else falls back to
// the Date constructor, which accepts human-readable strings like 'April 27, 2026'
// exactly as moment's deprecation path does.
// ---------------------------------------------------------------------------------
export const parseLenient = (
  input: string | number | Date | null | undefined,
  utcMode = false,
): AppDate | null => {
  if (input === null || input === undefined || input === '') return null;
  if (typeof input === 'string') {
    if (matchIsoShape(input)) {
      return utcMode ? parseStrictIso(input) : parseStrictIsoLocal(input);
    }
    const fallback = new Date(input);
    if (Number.isNaN(fallback.valueOf())) return null;
    return utcMode ? dayjs.utc(fallback.valueOf()) : dayjs(fallback.valueOf());
  }
  const parsed = utcMode ? dayjs.utc(input) : dayjs(input);
  return parsed.isValid() ? parsed : null;
};

/** Lenient parse in UTC mode — replaces `moment.utc(x)`. */
export const parseUtc = (input: string | number | Date | null | undefined): AppDate | null =>
  parseLenient(input, true);

/** Lenient parse in local mode — replaces `moment(x)`. */
export const parseLocal = (input: string | number | Date | null | undefined): AppDate | null =>
  parseLenient(input, false);

// ---------------------------------------------------------------------------------
// Now / zone primitives.
// ---------------------------------------------------------------------------------

/** Current time in the given IANA timezone, or local time if absent/unrecognised. */
export const nowInTz = (timezone?: string): AppDate =>
  timezone && isValidTimezone(timezone) ? dayjs().tz(timezone) : dayjs();

/** The browser/runtime IANA timezone. */
export const guessTz = (): string => dayjs.tz.guess();

// ---------------------------------------------------------------------------------
// Conversion pair absorbed from date-conversion.ts (ORI-187 families — the two
// control flows are intentionally NOT unified). `T | boolean` contracts preserved
// verbatim; `.tz()` never throws thanks to the isValidTimezone guard (moment's
// invalid-zone behavior is a logged no-op that leaves the instant in UTC mode).
// ---------------------------------------------------------------------------------

const parseUtcWithGivenFormat = (
  dateString: string,
  givenFormat?: DateParseFormat,
): AppDate | null => {
  if (typeof givenFormat === 'string') {
    const parsed = dayjs.utc(dateString, givenFormat);
    return parsed.isValid() ? parsed : null;
  }
  // Our ISO_8601 symbol — or moment.ISO_8601 passed by a not-yet-migrated caller.
  if (givenFormat !== undefined) return parseStrictIso(dateString);
  return parseUtc(dateString);
};

// Returns a formatted string when `formatTo` is set, otherwise the AppDate itself.
export const utcToZoned = <T extends string | AppDate>(
  dateString: string,
  timezone: string,
  formatTo?: string,
  givenFormat?: DateParseFormat,
): T | boolean => {
  if (dateString && timezone) {
    const formattedUtcTime = parseUtcWithGivenFormat(dateString, givenFormat);

    if (formattedUtcTime?.isValid()) {
      const convertToTimezone = isValidTimezone(timezone)
        ? formattedUtcTime.tz(timezone)
        : formattedUtcTime;

      if (formatTo) {
        return convertToTimezone.format(formatTo) as T;
      }

      return convertToTimezone as T;
    }
    return false;
  }
  return false;
};

export const utcToBrowserLocal = <T extends string | AppDate>(
  dateString: string,
  format?: string,
): T | boolean => {
  if (dateString) {
    const utcTime = parseStrictIso(dateString);
    if (utcTime?.isValid()) {
      const dateInLocal = dayjs(utcTime.valueOf());
      if (format) {
        return dateInLocal.format(format) as T;
      }
      return dateInLocal as T;
    }
  }
  return false;
};

// ---------------------------------------------------------------------------------
// S-01 — hand-rolled calendar bucketing. dayjs's calendar plugin buckets against the
// runner-local day and treats function results as final-but-unformatted strings;
// moment buckets against the reference adjusted to the DATE's utcOffset. Bucket
// thresholds are moment core's; function entries return the FINAL string.
// ---------------------------------------------------------------------------------
export type CalendarLabelBucket =
  | 'sameDay'
  | 'nextDay'
  | 'nextWeek'
  | 'lastDay'
  | 'lastWeek'
  | 'sameElse';

export type CalendarLabelEntry =
  | string
  | ((this: AppDate, date: AppDate, reference: AppDate) => string);

export type CalendarLabelFormats = Partial<Record<CalendarLabelBucket, CalendarLabelEntry>>;

/** moment's en-locale calendar defaults with LT/L pre-resolved (no localizedFormat plugin). */
const CALENDAR_LABEL_DEFAULTS: Record<CalendarLabelBucket, string> = {
  sameDay: '[Today at] h:mm A',
  nextDay: '[Tomorrow at] h:mm A',
  nextWeek: 'dddd [at] h:mm A',
  lastDay: '[Yesterday at] h:mm A',
  lastWeek: '[Last] dddd [at] h:mm A',
  sameElse: 'MM/DD/YYYY',
};

export const calendarLabel = (
  date: AppDate,
  reference: AppDate = dayjs(),
  formats: CalendarLabelFormats = {},
): string => {
  // moment: cloneWithOffset(reference, date).startOf('day') — the reference day is
  // evaluated in the DATE's utcOffset, so tz-localized instances bucket correctly.
  const startOfReferenceDay = reference.utcOffset(date.utcOffset()).startOf('day');
  const diff = date.diff(startOfReferenceDay, 'day', true);
  let bucket: CalendarLabelBucket;
  if (diff < -6) bucket = 'sameElse';
  else if (diff < -1) bucket = 'lastWeek';
  else if (diff < 0) bucket = 'lastDay';
  else if (diff < 1) bucket = 'sameDay';
  else if (diff < 2) bucket = 'nextDay';
  else if (diff < 7) bucket = 'nextWeek';
  else bucket = 'sameElse';

  const entry = formats[bucket] ?? CALENDAR_LABEL_DEFAULTS[bucket];
  if (typeof entry === 'function') {
    return entry.call(date, date, reference);
  }
  return date.format(entry);
};

// ---------------------------------------------------------------------------------
// S-08 — moment's exact duration decomposition (month bubbling via the Gregorian
// 146097-days-per-400-years constant). dayjs's duration plugin uses fixed 30-day
// months and leaves a .hours() remainder on ≥1-month durations.
// ---------------------------------------------------------------------------------
export type DurationParts = {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
};

const absFloor = (value: number): number => (value < 0 ? Math.ceil(value) || 0 : Math.floor(value));

const absCeil = (value: number): number => (value < 0 ? Math.floor(value) : Math.ceil(value));

export const durationParts = (totalSeconds: number): DurationParts => {
  const totalMilliseconds = totalSeconds * 1000;
  const milliseconds = totalMilliseconds % 1000;
  const wholeSeconds = absFloor(totalMilliseconds / 1000);
  const seconds = wholeSeconds % 60;
  const wholeMinutes = absFloor(wholeSeconds / 60);
  const minutes = wholeMinutes % 60;
  const wholeHours = absFloor(wholeMinutes / 60);
  const hours = wholeHours % 24;
  let days = absFloor(wholeHours / 24);
  // moment's bubble(): daysToMonths(days) = days * 4800 / 146097, then the month
  // total is converted back and REMOVED from days with absCeil.
  const monthsFromDays = absFloor((days * 4800) / 146097);
  let months = monthsFromDays;
  days -= absCeil((monthsFromDays * 146097) / 4800);
  const years = absFloor(months / 12);
  months %= 12;
  return { years, months, days, hours, minutes, seconds, milliseconds };
};

// ---------------------------------------------------------------------------------
// S-07 — DST-safe wall-clock rebuild. Naive `.startOf('day').add(N, 'day').hour(H)`
// keeps the pre-transition UTC offset on dayjs tz-instances; rebuilding from the
// resolved calendar-day string restores moment's wall-clock preservation.
// ---------------------------------------------------------------------------------
export const wallClockAtHour = (day: AppDate, hour: number, timezone?: string): AppDate => {
  // An invalid `day` formats to 'Invalid Date', which dayjs.tz() rejects with a
  // RangeError. Return an invalid AppDate instead so callers keep relying on their
  // own isValid() checks rather than a try/catch (moment never threw here).
  if (!day.isValid()) return dayjs(Number.NaN);
  const wallClock = `${day.format('YYYY-MM-DD')} ${String(hour).padStart(2, '0')}:00:00`;
  if (timezone && isValidTimezone(timezone)) {
    return dayjs.tz(wallClock, timezone);
  }
  return dayjs(wallClock);
};

// ---------------------------------------------------------------------------------
// S-04 — the zz plugin. dayjs's Intl-backed `z` has no tzdb abbreviation source, so
// `z`/`zz` token runs are resolved from the frozen ZONE_ABBREVIATIONS lookup with
// offset-based side selection. Replicates moment + moment-timezone exactly:
// - tz-attached instance → tzdb abbreviation for both `z` and `zz`;
// - utc-mode, no zone → `z` = 'UTC' (moment zoneAbbr), `zz` = 'Coordinated
//   Universal Time' (moment zoneName — golden-pinned by the unification suite);
// - plain local, no zone → '' for both (golden-pinned: '2026-04-27 ');
// - unknown zone → ''. Uppercase `Z` and [bracketed] literals are never touched.
// ---------------------------------------------------------------------------------
const JAN_PROBE = '2026-01-15T12:00:00Z';
const JUL_PROBE = '2026-07-15T12:00:00Z';

type TimezoneInternals = { $x?: { $timezone?: string } };

const lowercaseZoneIndex = new Map<string, string>();

const canonicalZoneKey = (zone: string): string | undefined => {
  // Own-property check ONLY: a plain `ZONE_ABBREVIATIONS[zone] !== undefined` test
  // succeeds for inherited Object.prototype members ('constructor', 'toString',
  // 'valueOf', …), which would then blow up on the tuple destructure below.
  if (Object.hasOwn(ZONE_ABBREVIATIONS, zone)) return zone;
  // moment-timezone's getZone() is case-insensitive; mirror it.
  if (lowercaseZoneIndex.size === 0) {
    for (const key of Object.keys(ZONE_ABBREVIATIONS)) {
      lowercaseZoneIndex.set(key.toLowerCase(), key);
    }
  }
  return lowercaseZoneIndex.get(zone.toLowerCase());
};

const zoneSideOffsetsCache = new Map<string, readonly [number, number]>();

const abbreviationForZone = (zone: string, instanceOffsetMinutes: number): string => {
  const key = canonicalZoneKey(zone);
  if (key === undefined) return '';
  // Read into a nullable local before destructuring — a miss must return '' (moment's
  // unknown-zone behavior), never throw on `undefined[0]`.
  const abbreviationPair: readonly [string, string] | undefined = ZONE_ABBREVIATIONS[key];
  if (abbreviationPair === undefined) return '';
  const [janAbbr, julAbbr] = abbreviationPair;
  if (janAbbr === julAbbr) return janAbbr;
  let sideOffsets = zoneSideOffsetsCache.get(key);
  if (!sideOffsets) {
    try {
      sideOffsets = [
        dayjs.utc(JAN_PROBE).tz(key).utcOffset(),
        dayjs.utc(JUL_PROBE).tz(key).utcOffset(),
      ];
    } catch {
      return '';
    }
    zoneSideOffsetsCache.set(key, sideOffsets);
  }
  if (instanceOffsetMinutes === sideOffsets[1]) return julAbbr;
  if (instanceOffsetMinutes === sideOffsets[0]) return janAbbr;
  // Offset matches neither frozen side (historic/post-2026 rule change): nearest side.
  return Math.abs(instanceOffsetMinutes - sideOffsets[0]) <=
    Math.abs(instanceOffsetMinutes - sideOffsets[1])
    ? janAbbr
    : julAbbr;
};

/** Public lookup: tzdb abbreviation for a zone at an instant (default: now). */
export const zoneAbbreviation = (timezone: string, at?: AppDate): string => {
  if (!isValidTimezone(timezone)) return '';
  const instant = at ?? dayjs();
  try {
    return abbreviationForZone(timezone, dayjs.utc(instant.valueOf()).tz(timezone).utcOffset());
  } catch {
    return '';
  }
};

/** [`z` value, `zz` value] for an instance — moment's zoneAbbr/zoneName split. */
const zoneTokenValues = (instance: Dayjs): readonly [string, string] => {
  const zone = (instance as unknown as TimezoneInternals).$x?.$timezone;
  if (zone) {
    const abbreviation = abbreviationForZone(zone, instance.utcOffset());
    return [abbreviation, abbreviation];
  }
  if (instance.isUTC()) return ['UTC', 'Coordinated Universal Time'];
  return ['', ''];
};

const HAS_LOWERCASE_Z = /z/;
// Bracketed literals are captured FIRST so `z` inside them is never rewritten; the
// greedy z{1,2} run decomposition ('zzz' → 'zz' + 'z') matches moment's tokenizer.
const Z_TOKEN_RUNS = /(\[[^\]]*\])|z{1,2}/g;

const zoneAbbrPlugin: PluginFunc = (_option, dayjsClass) => {
  const proto = dayjsClass.prototype;
  const originalFormat = proto.format;
  proto.format = function (this: Dayjs, template?: string): string {
    if (!template || !HAS_LOWERCASE_Z.test(template)) {
      return originalFormat.call(this, template);
    }
    // Invalid instances short-circuit to dayjs's own 'Invalid Date' — they must NOT
    // reach the substitution below, where a zone-less invalid instance would fall
    // into the empty-template path and render '' instead.
    if (!this.isValid()) {
      return originalFormat.call(this, template);
    }
    const [zValue, zzValue] = zoneTokenValues(this);
    const transformed = template.replace(Z_TOKEN_RUNS, (match, bracketed: string | undefined) => {
      if (bracketed !== undefined) return bracketed;
      const value = match === 'z' ? zValue : zzValue;
      return value === '' ? '' : `[${value}]`;
    });
    // A z/zz-ONLY template on a zone-less instance substitutes down to ''. dayjs (and
    // advancedFormat) treat a falsy format string as "use FORMAT_DEFAULT", so handing
    // '' back to originalFormat would render the whole ISO timestamp where moment
    // renders ''. Real call site: useNotifications.tsx passes formatTo: 'z'.
    if (transformed === '') return '';
    return originalFormat.call(this, transformed);
  };
};

// ---------------------------------------------------------------------------------
// Plugin registration — order is load-bearing. relativeTime keeps its DEFAULT
// thresholds (ledger S-02 proves they already reproduce moment's boundaries);
// updateLocale is registered now and consumed in PR-2 (en-workqueue preset);
// zoneAbbrPlugin is LAST so its format hook wraps advancedFormat's.
// ---------------------------------------------------------------------------------
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(duration);
dayjs.extend(calendar);
dayjs.extend(zoneAbbrPlugin);

// ---------------------------------------------------------------------------------
// en-workqueue — the abbreviated relative-time locale for the Work Queue (APX-561,
// PR-2). Registered here (the only module allowed to import dayjs) as a clone of the
// dayjs `en` locale with moment's en-workqueue relativeTime overrides. The third
// `dayjs.locale(preset, undefined, true)` arg registers the preset WITHOUT switching
// the global locale, so consumers opt in per instance via `.locale(WORK_QUEUE_LOCALE)`
// and every other `.fromNow()` surface stays on the default long-form English. Parity
// with moment's defineLocale variant is pinned by the S-03 spike, except months: since
// ORI-1001 `M`/`MM` render "mo" so they can't be mistaken for minutes (`m`/`mm`).
// dayjs has no `ss` token (moment did), so `s` covers the whole 0-44s bucket.
// ---------------------------------------------------------------------------------
export const WORK_QUEUE_LOCALE = 'en-workqueue';

dayjs.locale(
  {
    ...en,
    name: WORK_QUEUE_LOCALE,
    relativeTime: {
      future: 'in %s',
      past: '%s ago',
      s: 'Just now',
      m: '1m',
      mm: '%dm',
      h: '1h',
      hh: '%dh',
      d: '1d',
      dd: '%dd',
      M: '1mo',
      MM: '%dmo',
      y: '1y',
      yy: '%dy',
    },
  },
  undefined,
  true,
);

/**
 * The configured dayjs factory — import from here, never from 'dayjs'.
 *
 * SCOPE WARNING: `dayjs` is a MODULE SINGLETON, and `zoneAbbrPlugin` patches
 * `Dayjs.prototype.format`. Both the plugin registrations above and the z/zz format
 * override are therefore PROCESS-WIDE: once this module is loaded, EVERY import of
 * 'dayjs' anywhere in the bundle (app code, tests, third-party packages) sees the
 * patched prototype — this re-export is not an isolated instance. The Biome rule
 * banning direct 'dayjs' imports outside this file is what keeps that invariant
 * reviewable: it guarantees the patched behavior is only ever reached through this
 * adapter's exports, which are the surface the parity tests pin.
 */
export { dayjs };
